import React, { useEffect, useMemo, useState } from 'react';
import { Select, Row, Col } from 'antd';
import { uniqueId, map } from 'lodash';
import {
  TextInput,
  AlertBox,
  SelectInput,
  Button,
  UtilData,
} from '@life-line-lab/components-react';
import { useTranslation } from 'react-i18next';
const statesCities = UtilData.StateCityList.country_MY;
import { countriesStates } from 'Data/countriesStates';

import { StateSelector, CitySelector } from 'Components/shared/select-inputs-with-data';
import { ShallowClonedTypeFor } from 'Types/common.types';
import usePreviousValue from 'Hooks/previous-value.hook';
// import './address-input.scss';

export type AddressInputValues = {
  addressLine1: string;
  addressLine2: string;
  country: string;
  state: string;
  stateCode: string;
  cityCode: string;
  city: string;
  postcode: string;
};
type Props = {
  isRequired?: boolean;
  isReadonly?: boolean;
  input: AddressInputValues;
  error: ShallowClonedTypeFor<AddressInputValues, string>;
  handleSelectChange: (value: any, name: keyof AddressInputValues) => void;
};

const AddressInput = ({ isReadonly, input, error, handleSelectChange }: Props) => {
  const { t } = useTranslation();
  const lastState = usePreviousValue(input.state);

  const [initialInput, setInitialInput] = useState(input);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleSelectChange(event.target.value, event.target.name as any);
  };

  const countryOptions = useMemo(() => {
    return countriesStates.map((cs) => ({
      label: cs.name,
      value: cs.code,
    }));
  }, [countriesStates]);

  useEffect(() => {
    if (!lastState) return;

    if (initialInput.state === input.state) {
      return;
    }
    setInitialInput(input);
    handleSelectChange('', 'city');
  }, [input.state]);

  return (
    <div>
      <Row gutter={16}>
        <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <TextInput
            placeholder={t('label.addressLine1')}
            disabled={true}
            className="text-input"
            isError={!!error.addressLine1}
            isRequired={true}
            isReadonly={isReadonly}
            label={t('label.addressLine1')}
            name="addressLine1"
            value={input.addressLine1}
            onChange={(event) => handleTextChange(event)}
          />
          <AlertBox text={error.addressLine1} />
        </Col>

        <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <TextInput
            className="text-input"
            placeholder={t('label.addressLine2')}
            isError={!!error.addressLine2}
            isRequired={false}
            isReadonly={isReadonly}
            label={t('label.addressLine2')}
            name="addressLine2"
            value={input.addressLine2}
            onChange={(event) => handleTextChange(event)}
          />
          <AlertBox text={error.addressLine2} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <SelectInput
            placeholder={t('label.country')}
            label={t('label.country')}
            className="text-input"
            isRequired={true}
            isReadonly={true}
            isError={!!error.country}
            name="country"
            value={input.country}
            onChange={(value: string | string[]) => handleSelectChange(value, 'country')}
            options={countryOptions}
          />
          <AlertBox text={error.country} />
        </Col>

        <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <StateSelector
            placeholder={t('label.state')}
            label={t('label.state')}
            className="text-input"
            isRequired={true}
            isReadonly={isReadonly}
            isError={!!error.state}
            name="state"
            disabled={!input.country ? true : false}
            value={input.state}
            onChange={(value: string | string[]) => handleSelectChange(value, 'state')}
            additionalFilter={{
              countryCode: input.country,
            }}
          />
          <AlertBox text={error.state} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <CitySelector
            className="text-input"
            placeholder={t('label.city')}
            isError={!!error.city}
            isRequired={true}
            isReadonly={isReadonly}
            label={t('label.city')}
            name="city"
            value={input.city}
            onChange={(value: string | string[]) => handleSelectChange(value, 'city')}
            additionalFilter={{
              countryCode: input.country,
              state: input.state,
            }}
          />
          <AlertBox text={error.city} />
        </Col>

        <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <TextInput
            className="text-input"
            placeholder={t('label.postcode')}
            isError={!!error.postcode}
            isReadonly={isReadonly}
            isRequired={true}
            label={t('label.postcode')}
            name="postcode"
            value={input.postcode}
            onChange={(event) => handleTextChange(event)}
          />
          <AlertBox text={error.postcode} />
        </Col>
      </Row>
    </div>
  );
};

export default AddressInput;
